/**
 * BackupPoliciesList — Políticas de backup (estándar de listados del CMDB).
 *
 * Antes: tabla propia (`wol-table`), diálogo de borrado propio con
 * `window.confirm` y la ficha abriendo **directamente en edición**.
 *
 * Ahora: `ListPage` con buscador, filtros, columnas, vistas y export; la ficha
 * entra en **lectura** y el borrado usa el diálogo único del CMDB.
 *
 * Botones propios:
 *  · **Ver el destino** — diálogo con el repositorio, el destino y la copia
 *    offsite (con su detalle), más el cifrado.
 *  · **Ver el software protegido** — a la ficha del servicio en el Catálogo.
 *  · **Ver la política origen** — cuando esta política es una copia secundaria
 *    (`source_policy_id`), salta a la política de la que copia.
 *  · **Ver en el Diseñador** — al grafo del servicio protegido.
 *  · **Copiar** — alta precargada sin targets.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CloudArrowDownIcon,
  ListBulletIcon,
  Squares2X2Icon,
  CircleStackIcon,
  WrenchScrewdriverIcon,
  ShareIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import BackupPolicyForm from './BackupPolicyForm';
import { useListView } from '../views/ViewsContext';
import { useAutoOpen } from '../hooks/useAutoOpen';
import { getServerFormStyle } from '../utils/serverFormStyle';
import api from '../services/api';
import useNavOrder from '../hooks/useNavOrder';

const STATUS_META = {
  active:   { label: 'Activa',   badge: 'list-badge--on' },
  disabled: { label: 'Inactiva', badge: 'list-badge--off' },
  error:    { label: 'Con error', badge: 'list-badge--warn' },
};

const LAST_RUN_META = {
  success: { label: 'Correcta',   badge: 'list-badge--on' },
  failed:  { label: 'Con error',  badge: 'list-badge--off' },
  partial: { label: 'Parcial',    badge: 'list-badge--warn' },
  unknown: { label: 'Sin datos',  badge: 'list-badge' },
};

const EMPTY_POLICY = {
  name: '', software_service_id: '', job_type: 'backup', backup_type: '',
  status: 'active', retention_days: '', notes: '',
};

export default function BackupPoliciesList({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [policies, setPolicies] = useState([]);
  const [meta, setMeta]         = useState({});
  const [services, setServices] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');

  const [filterJobType, setFilterJobType]     = useState('');
  const [filterType, setFilterType]           = useState('');
  const [filterStatus, setFilterStatus]       = useState('');
  const [filterFrequency, setFilterFrequency] = useState('');
  const [filterOffsite, setFilterOffsite]     = useState('');
  const [filterSoftware, setFilterSoftware]   = useState(() => searchParams.get('software') || '');
  const [filterEncryption, setFilterEncryption] = useState('');
  const [filterLastRun, setFilterLastRun]     = useState('');
  const [groupMode, setGroupMode]             = useState('');

  const [selected, setSelected]       = useState(null);
  const [initialMode, setInitialMode] = useState('edit');
  const [cloneSource, setCloneSource] = useState(null);
  const [hlId, setHlId]               = useState(null);
  const [destinationOf, setDestinationOf] = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [m, list, svcs] = await Promise.all([
        api.getBackupMeta(companyId),
        api.getBackupPolicies(companyId),
        api.getServices(companyId),
      ]);
      setMeta(m || {});
      setPolicies(Array.isArray(list) ? list : []);
      setServices(Array.isArray(svcs) ? svcs : (svcs.services || []));
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las políticas');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* ?software=<id>: abre la lista filtrada por el servicio de backup. */
  useEffect(() => {
    const sw = searchParams.get('software');
    if (sw) setFilterSoftware(String(sw));
  }, [searchParams]);

  /* ?destacar=<id>[&abrir=1] */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !policies.length) return undefined;
    const id = Number(raw);
    const target = policies.find(p => p.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { setCloneSource(null); setInitialMode('view'); setSelected(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`bkpol-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, policies, setSearchParams]);

  /* ── ficha ──────────────────────────────────────────────────────────────── */
  const openView   = (p) => { setCloneSource(null); setInitialMode('view');   setSelected(p); };
  const openEdit   = (p) => { setCloneSource(null); setInitialMode('edit');   setSelected(p); };
  const openCreate = ()  => { setCloneSource(null); setInitialMode('create'); setSelected({ ...EMPTY_POLICY }); };
  /** Copiar: se lleva la configuración, pero no los targets ni el origen. */
  const openCopy   = (p) => {
    if (!p) return;
    setSelected(null);
    setCloneSource({
      ...p,
      id: undefined,
      name: p.name ? `${p.name} (copia)` : '',
      source_policy_id: null,
      source_policy_name: null,
      last_run_at: null,
      last_run_status: null,
      targets: [],
    });
    setInitialMode('create');
  };
  const closeForm = () => { setSelected(null); setCloneSource(null); load(); };
  useAutoOpen(policies, openEdit);

  const handleDelete = async (p) => {
    try {
      await api.deleteBackupPolicy(p.id, companyId);
      await load();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar');
    }
  };

  /* ── etiquetas y opciones ───────────────────────────────────────────────── */
  const backupTypeLabel = useCallback(
    (value) => (meta.backup_type_catalog || []).find(x => x.value === value)?.label || value || '—',
    [meta.backup_type_catalog],
  );
  const softwareLabel = useCallback(
    (p) => p.software_service_name
      || (p.software_service_id
        ? (services.find(s => s.id === p.software_service_id)?.display_name
          || services.find(s => s.id === p.software_service_id)?.name)
        : null),
    [services],
  );

  const jobTypeOptions = useMemo(() => {
    const seen = new Map();
    policies.forEach(p => { if (p.job_type) seen.set(p.job_type, p.job_type_label || p.job_type); });
    (meta.job_types || []).forEach(j => { if (!seen.has(j.value)) seen.set(j.value, j.label); });
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }, [policies, meta.job_types]);

  const softwareOptions = useMemo(() => {
    const seen = new Map();
    policies.forEach(p => {
      if (p.software_service_id) seen.set(String(p.software_service_id), softwareLabel(p) || `#${p.software_service_id}`);
    });
    return [...seen.entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1]), 'es'))
      .map(([value, label]) => ({ value, label }));
  }, [policies, softwareLabel]);

  const destinationLabel = (p) => {
    const principal = p.repository_name || p.destination_detail || p.destination_type_label
      || p.destination_type || '—';
    if (p.offsite_repository_name) return `${principal} · offsite: ${p.offsite_repository_name}`;
    if (p.is_offsite) return `${principal} · offsite`;
    return principal;
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return policies.filter((p) => {
      if (filterJobType && p.job_type !== filterJobType) return false;
      if (filterType && p.backup_type !== filterType) return false;
      if (filterStatus && p.status !== filterStatus) return false;
      if (filterFrequency && p.schedule_frequency !== filterFrequency) return false;
      if (filterOffsite === 'yes' && !p.is_offsite && !p.offsite_repository_name) return false;
      if (filterOffsite === 'no' && (p.is_offsite || p.offsite_repository_name)) return false;
      if (filterSoftware && String(p.software_service_id || '') !== String(filterSoftware)) return false;
      if (filterEncryption === 'yes' && !p.encryption) return false;
      if (filterEncryption === 'no' && p.encryption) return false;
      if (filterLastRun && (p.last_run_status || 'unknown') !== filterLastRun) return false;
      if (!term) return true;
      return [
        p.name, p.software_service_name, p.repository_name, p.destination_detail,
        p.schedule_summary, p.notes, p.offsite_repository_name,
        p.job_type_label, p.backup_type_label,
      ].filter(Boolean).join(' ').toLowerCase().includes(term);
    });
  }, [policies, search, filterJobType, filterType, filterStatus, filterFrequency,
    filterOffsite, filterSoftware, filterEncryption, filterLastRun]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navPolicies = useNavOrder(filtered, selected, selected !== null);

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: tr('bkpol.name', 'Nombre'),
        sortable: true,
        render: (p) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>{p.name}</span>
            <span className="list-hint">{p.job_type_label || 'Copia de seguridad'}</span>
          </span>
        ),
      },
      {
        key: 'software',
        label: tr('bkpol.software', 'Software'),
        sortable: true,
        sortValue: (p) => softwareLabel(p) || '',
        render: (p) => {
          const label = softwareLabel(p);
          if (!label) return dash;
          return (
            <button
              type="button"
              className="list-badge"
              style={{ cursor: 'pointer' }}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/services?destacar=${p.software_service_id}&abrir=1`);
              }}
              title={tr('bkpol.view_software', 'Ver el software protegido')}
            >
              {label}
            </button>
          );
        },
      },
      {
        key: 'schedule',
        label: tr('bkpol.schedule', 'Programación'),
        sortable: true,
        sortValue: (p) => p.schedule_frequency || '',
        render: (p) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span>{p.schedule_summary || p.schedule || dash}</span>
            {p.copy_trigger === 'after_backup' && (
              <span className="list-hint">{tr('bkpol.after_backup', 'Al terminar el backup')}</span>
            )}
          </span>
        ),
      },
      {
        key: 'retention_days',
        label: tr('bkpol.retention', 'Retención'),
        sortable: true,
        sortValue: (p) => p.retention_days ?? 0,
        render: (p) => (p.retention_days != null ? `${p.retention_days} d` : dash),
      },
      {
        key: 'backup_type',
        label: tr('bkpol.type', 'Tipo'),
        sortable: true,
        sortValue: (p) => p.backup_type || '',
        render: (p) => (
          <span
            className="list-badge"
            title={p.backup_type_description || p.job_type_description || ''}
          >
            {backupTypeLabel(p.backup_type)}
          </span>
        ),
      },
      {
        key: 'destination',
        label: tr('bkpol.destination', 'Destino'),
        sortable: true,
        sortValue: (p) => p.repository_name || p.destination_detail || '',
        render: (p) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span>{destinationLabel(p)}</span>
            {p.encryption && (
              <span className="list-badge" title={tr('bkpol.encrypted', 'Cifrada')}>
                {tr('bkpol.encrypted', 'Cifrada')}
              </span>
            )}
          </span>
        ),
      },
      {
        key: 'last_run',
        label: tr('bkpol.last_run', 'Última ejecución'),
        sortable: true,
        sortValue: (p) => p.last_run_at || '',
        render: (p) => {
          if (!p.last_run_at && !p.last_run_status) return dash;
          const meta_ = LAST_RUN_META[p.last_run_status || 'unknown'] || LAST_RUN_META.unknown;
          return (
            <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
              <span className={`list-badge ${meta_.badge}`}>{meta_.label}</span>
              {p.last_run_at && <span className="list-hint">{new Date(p.last_run_at).toLocaleString()}</span>}
            </span>
          );
        },
      },
      {
        key: 'status',
        label: tr('ipam.status', 'Estado'),
        sortable: true,
        sortValue: (p) => p.status || 'active',
        render: (p) => {
          const meta_ = STATUS_META[p.status] || { label: p.status || '—', badge: '' };
          return <span className={`list-badge ${meta_.badge}`}>{meta_.label}</span>;
        },
      },
      {
        key: 'source',
        label: tr('bkpol.source', 'Copia de'),
        render: (p) => (p.source_policy_name
          ? (
            <button
              type="button"
              className="list-badge"
              style={{ cursor: 'pointer' }}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/backups?destacar=${p.source_policy_id}&abrir=1`);
              }}
              title={tr('bkpol.view_source', 'Ver la política de la que copia')}
            >
              {p.source_policy_name}
            </button>
          )
          : dash),
      },
      {
        key: 'notes',
        label: tr('description', 'Notas'),
        render: (p) => p.notes || dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, navigate, softwareLabel, backupTypeLabel, destinationLabel]);

  const defaultVisible = ['name', 'software', 'schedule', 'backup_type', 'destination', 'last_run', 'status'];

  const filters = [
    {
      key: 'job', label: tr('bkpol.job_type', 'Trabajo'), allLabel: tr('bkpol.job_all', 'Cualquier trabajo'),
      value: filterJobType, onChange: setFilterJobType, options: jobTypeOptions,
    },
    {
      key: 'type', label: tr('bkpol.type', 'Tipo de backup'), allLabel: tr('bkpol.type_all', 'Cualquier tipo'),
      value: filterType, onChange: setFilterType,
      options: (meta.backup_type_catalog || []).map(x => ({ value: x.value, label: x.label })),
    },
    {
      key: 'status', label: tr('ipam.status', 'Estado'), allLabel: tr('ipam.status_all', 'Todos los estados'),
      value: filterStatus, onChange: setFilterStatus,
      options: (meta.statuses || Object.keys(STATUS_META)).map(s => ({
        value: s, label: (STATUS_META[s] || {}).label || s,
      })),
    },
    {
      key: 'frequency', label: tr('bkpol.frequency', 'Programación'), allLabel: tr('bkpol.freq_all', 'Cualquier programación'),
      value: filterFrequency, onChange: setFilterFrequency,
      options: (meta.schedule_frequencies || []).map(f => ({ value: f.value, label: f.label })),
    },
    {
      key: 'offsite', label: 'Offsite', allLabel: tr('bkpol.offsite_all', 'Offsite: cualquiera'),
      value: filterOffsite, onChange: setFilterOffsite,
      options: [
        { value: 'yes', label: tr('bkpol.offsite_yes', 'Con copia fuera') },
        { value: 'no', label: tr('bkpol.offsite_no', 'Sin copia fuera') },
      ],
    },
    {
      key: 'software', label: tr('bkpol.software', 'Software'), allLabel: tr('bkpol.software_all', 'Cualquier software'),
      value: filterSoftware, onChange: setFilterSoftware, options: softwareOptions,
    },
    {
      key: 'encryption', label: tr('bkpol.encryption', 'Cifrado'), allLabel: tr('bkpol.encryption_all', 'Con o sin cifrado'),
      value: filterEncryption, onChange: setFilterEncryption,
      options: [
        { value: 'yes', label: tr('bkpol.encrypted', 'Cifradas') },
        { value: 'no', label: tr('bkpol.unencrypted', 'Sin cifrar') },
      ],
    },
    {
      key: 'last', label: tr('bkpol.last_run', 'Última ejecución'), allLabel: tr('bkpol.last_all', 'Cualquier resultado'),
      value: filterLastRun, onChange: setFilterLastRun,
      options: (meta.last_run_statuses || Object.keys(LAST_RUN_META)).map(s => ({
        value: s, label: (LAST_RUN_META[s] || {}).label || s,
      })),
    },
    {
      key: 'group', label: tr('bkpol.group', 'Agrupar por'), allLabel: tr('bkpol.group_none', 'Sin agrupar'),
      value: groupMode, onChange: setGroupMode,
      options: [
        { value: 'software', label: tr('bkpol.software', 'Software') },
        { value: 'type', label: tr('bkpol.type', 'Tipo') },
        { value: 'destination', label: tr('bkpol.destination', 'Destino') },
      ],
    },
  ];

  const groupBy = useMemo(() => {
    if (groupMode === 'software') return (p) => softwareLabel(p) || tr('bkpol.no_software', 'Sin software');
    if (groupMode === 'type') return (p) => backupTypeLabel(p.backup_type) || tr('list.no_value', 'Sin valor');
    if (groupMode === 'destination') return (p) => p.repository_name || p.destination_type_label || tr('bkpol.no_destination', 'Sin destino');
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupMode, softwareLabel, backupTypeLabel]);

  const subtitle = `${policies.length} ${tr('bkpol.plural', 'políticas')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtradas')}`;

  const exportRows = useMemo(() => filtered.map(p => ({
    'Nombre': p.name || '',
    'Trabajo': p.job_type_label || '',
    'Tipo': backupTypeLabel(p.backup_type),
    'Software': softwareLabel(p) || '',
    'Programación': p.schedule_summary || p.schedule || '',
    'Frecuencia': p.schedule_frequency || '',
    'Hora': p.schedule_time || '',
    'Retención (días)': p.retention_days ?? '',
    'Destino': destinationLabel(p),
    'Repositorio': p.repository_name || '',
    'Offsite': p.is_offsite ? 'Sí' : 'No',
    'Repositorio offsite': p.offsite_repository_name || '',
    'Cifrado': p.encryption ? 'Sí' : 'No',
    'Copia de': p.source_policy_name || '',
    'Última ejecución': p.last_run_at ? new Date(p.last_run_at).toLocaleString() : '',
    'Resultado': (LAST_RUN_META[p.last_run_status || 'unknown'] || LAST_RUN_META.unknown).label,
    'Estado': (STATUS_META[p.status] || {}).label || p.status || '',
  })), [filtered, softwareLabel, backupTypeLabel]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Tarjetas: lo esencial de la política, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((p) => {
          const st = STATUS_META[p.status] || { label: p.status || '—', badge: '' };
          return (
            <div
              key={p.id}
              id={`bkpol-card-${p.id}`}
              className={`list-card${p.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openView(p)}
              onKeyDown={(event) => { if (event.key === 'Enter') openView(p); }}
              title={`${tr('action.view', 'Ver')} ${p.name}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">{p.name}</div>
                  <span className="list-hint">{p.job_type_label || ''}</span>
                </div>
                <span className={`list-badge ${st.badge}`}>{st.label}</span>
              </div>

              <div className="list-card-meta">
                {softwareLabel(p) && <span>{softwareLabel(p)}</span>}
                <span className="list-badge">{backupTypeLabel(p.backup_type)}</span>
                {p.retention_days != null && <span className="list-hint">{p.retention_days} días</span>}
                {p.encryption && <span className="list-badge">{tr('bkpol.encrypted', 'Cifrada')}</span>}
                {(p.is_offsite || p.offsite_repository_name) && <span className="list-badge">Offsite</span>}
                <span className="list-hint">{destinationLabel(p)}</span>
              </div>

              <div className="list-card-actions">{rowActions(p)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  const BK_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada política'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId]);
  useListView('backup_policies', 'list');

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">
          {tr('bkpol.select_company', 'Selecciona una empresa para ver las políticas de backup.')}
        </div>
      </div>
    );
  }

  return (
    <>
      {(selected !== null || cloneSource) && (
        <BackupPolicyForm
          policy={cloneSource || (selected && selected.id ? selected : null)}
          companyId={companyId}
          meta={meta}
          services={services}
          policies={policies}
          initialMode={cloneSource ? 'create' : initialMode}
          variant={getServerFormStyle() === 'cards' ? 'cards' : 'classic'}
          navItems={navPolicies}
          navIndex={!cloneSource && selected && selected.id ? navPolicies.findIndex(p => String(p.id) === String(selected.id)) : -1}
          onNavigate={(destino) => setSelected(destino)}
          onClose={closeForm}
          onSaved={(creado) => {
            load();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setInitialMode('edit'); setSelected(creado); }
          }}
        />
      )}

      <div style={{ display: (selected !== null || cloneSource) ? 'none' : undefined }}>
        <ListPage
          icon={CloudArrowDownIcon}
          title={tr('bkpol.title', 'Políticas de backup')}
          subtitle={subtitle}

          onCreate={openCreate}
          createLabel={tr('bkpol.new', 'Nueva política')}
          createTitle={tr('bkpol.new_hint', 'Política de protección: qué se copia, cada cuánto y dónde')}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('bkpol.search', 'Buscar por nombre, software, repositorio…')}
          filters={filters}

          exportRows={exportRows}
          exportFileBase="politicas-backup"
          exportSheetName="Políticas de backup"

          columns={columns}
          columnStorageKey="cmdb_cols_backup_policies"
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(p) => p.id}
          rowId={(p) => `bkpol-row-${p.id}`}
          rowClassName={(p) => (p.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={openView}
          rowTitle={(p) => `${tr('action.view', 'Ver')} ${p.name}`}
          groupBy={groupBy}

          views={BK_VIEWS}
          listKey="backup_policies"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando políticas…')}
          error={error}

          empty={{
            icon: '💾',
            title: policies.length === 0
              ? tr('bkpol.empty', 'No hay políticas')
              : tr('msg.no_results', 'Ninguna política coincide con los filtros'),
            subtitle: policies.length === 0
              ? tr('bkpol.empty_hint', 'Define una política para proteger servidores, VMs o servicios.')
              : undefined,
            action: policies.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={openCreate}>
                ＋ {tr('bkpol.new', 'Nueva política')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (p) => (
              <>
                {/* Ver el destino (repositorio + offsite + cifrado) */}
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => setDestinationOf(p)}
                  title={tr('bkpol.view_destination', 'Ver el destino')}
                >
                  <CircleStackIcon />
                </button>
                {/* Ver el software protegido */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!p.software_service_id}
                  onClick={() => navigate(`/services?destacar=${p.software_service_id}&abrir=1`)}
                  title={softwareLabel(p)
                    ? `${tr('bkpol.view_software', 'Ver el software protegido')}: ${softwareLabel(p)}`
                    : tr('bkpol.no_software', 'Sin software')}
                >
                  <WrenchScrewdriverIcon />
                </button>
                {/* Ver la política de la que copia */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!p.source_policy_id}
                  onClick={() => navigate(`/backups?destacar=${p.source_policy_id}&abrir=1`)}
                  title={p.source_policy_name
                    ? `${tr('bkpol.view_source', 'Ver la política de la que copia')}: ${p.source_policy_name}`
                    : tr('bkpol.no_source', 'No copia de otra política')}
                >
                  <ArrowUturnLeftIcon />
                </button>
                {/* Ver el servicio en el Diseñador */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!p.software_service_id}
                  onClick={() => navigate(`/services/designer?service=${p.software_service_id}`)}
                  title={tr('virt.view_in_designer', 'Ver en el Diseñador de servicios')}
                >
                  <ShareIcon />
                </button>
              </>
            ),
            onCopy: openCopy,
            onEdit: openEdit,
            onDelete: handleDelete,
            deleteTitle: tr('bkpol.delete_title', 'Eliminar política'),
            deleteMessage: (p) => (
              <>
                {tr('virt.delete_confirm', '¿Seguro que quieres eliminar')} <strong>{p.name}</strong>?{' '}
                {tr('bkpol.delete_used', 'Se eliminarán también sus destinos (targets) y su programación.')}
              </>
            ),
          }}
        />
      </div>

      {/* ══ Diálogo: destino de la copia ══ */}
      {destinationOf && (
        <Dialog
          title={`${tr('bkpol.destination_of', 'Destino de')} ${destinationOf.name}`}
          icon={<CircleStackIcon style={{ width: 18, height: 18 }} />}
          maxWidth={640}
          onClose={() => setDestinationOf(null)}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => setDestinationOf(null)}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span className="list-hint" style={{ minWidth: 130 }}>{tr('bkpol.dest_type', 'Tipo de destino')}</span>
              <span className="list-badge">{destinationOf.destination_type_label || destinationOf.destination_type || '—'}</span>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span className="list-hint" style={{ minWidth: 130 }}>{tr('bkpol.dest_detail', 'Detalle')}</span>
              <span>{destinationOf.destination_detail || '—'}</span>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span className="list-hint" style={{ minWidth: 130 }}>{tr('bkpol.repository', 'Repositorio')}</span>
              <span>
                {destinationOf.repository_name || '—'}
                {destinationOf.repository_type_label && (
                  <span className="list-badge" style={{ marginLeft: 8 }}>{destinationOf.repository_type_label}</span>
                )}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span className="list-hint" style={{ minWidth: 130 }}>{tr('bkpol.repo_path', 'Ruta física')}</span>
              <span className="list-code">{destinationOf.repository_physical_name || '—'}</span>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span className="list-hint" style={{ minWidth: 130 }}>Offsite</span>
              <span>
                {(destinationOf.is_offsite || destinationOf.offsite_repository_name)
                  ? (
                    <>
                      <span className="list-badge">{destinationOf.offsite_repository_name || tr('bkpol.offsite_yes', 'Con copia fuera')}</span>
                      {destinationOf.offsite_detail && <div className="list-hint">{destinationOf.offsite_detail}</div>}
                      {destinationOf.offsite_repository_physical_name && (
                        <div className="list-code list-hint">{destinationOf.offsite_repository_physical_name}</div>
                      )}
                    </>
                  )
                  : <span className="list-hint">{tr('bkpol.offsite_no', 'Sin copia fuera')}</span>}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span className="list-hint" style={{ minWidth: 130 }}>{tr('bkpol.encryption', 'Cifrado')}</span>
              <span className={`list-badge ${destinationOf.encryption ? 'list-badge--on' : ''}`.trim()}>
                {destinationOf.encryption ? tr('bkpol.encrypted', 'Cifrada') : tr('bkpol.unencrypted', 'Sin cifrar')}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span className="list-hint" style={{ minWidth: 130 }}>{tr('bkpol.retention', 'Retención')}</span>
              <span>{destinationOf.retention_days != null ? `${destinationOf.retention_days} días` : '—'}</span>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}
